import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { PageHero } from '@/components/PageHero';
import { CTASection } from '@/components/CTASection';
import { Reveal } from '@/components/Reveal';
import { SEO } from '@/components/SEO';
import { Skeleton } from '@/components/ui/skeleton';
import { getJournalPostBySlug } from '@/lib/api';
import { JOURNAL_POST_PAGE } from '@/constants/testIds';
import NotFound from '@/pages/NotFound';
import { SkeletonList } from '@/components/SkeletonList';
import { keyedList } from '@/lib/keys';

export default function JournalPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setPost(null);
    setNotFound(false);
    getJournalPostBySlug(slug)
      .then(setPost)
      .catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) return <NotFound />;

  const structuredData = post
    ? {
        '@context': 'https://schema.org',
        '@type': 'BlogPosting',
        headline: post.title,
        description: post.excerpt,
        image: post.cover_image_url ? [post.cover_image_url] : undefined,
        author: { '@type': 'Organization', name: post.author || 'LXRA Studios' },
        publisher: { '@type': 'Organization', name: 'LXRA Studios' },
        datePublished: post.published_at,
        keywords: post.tags?.join(', ') || undefined,
      }
    : undefined;

  return (
    <div data-testid={JOURNAL_POST_PAGE.root}>
      {post && <SEO title={post.title} description={post.excerpt} image={post.cover_image_url} structuredData={structuredData} />}
      {post ? (
        <PageHero
          kicker={post.tags?.join(' · ') || 'Journal'}
          title={post.title.toUpperCase()}
          subtitle={post.excerpt}
          backgroundImage={post.cover_image_url}
          height="min-h-[46vh] lg:min-h-[52vh]"
        />
      ) : (
        <div className="min-h-[46vh] lg:min-h-[52vh] bg-[#0F0F10]" />
      )}

      <section className="bg-[#0A0A0A]">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-10 py-12 lg:py-16">
          <Link
            to="/journal"
            data-testid={JOURNAL_POST_PAGE.backLink}
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.12em] text-[hsl(var(--lxra-muted))] hover:text-[hsl(var(--lxra-gold))] transition-colors mb-10"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Journal
          </Link>

          {!post && (
            <div className="space-y-4">
              <SkeletonList count={5} className="h-4 w-full rounded bg-[#141416]" />
            </div>
          )}

          {post && (
            <Reveal>
              <article className="prose prose-invert max-w-none">
                {keyedList(post.content.split('\n\n')).map(({ key, item: paragraph }) => (
                  <p key={key} className="text-[hsl(var(--lxra-fg))]/90 leading-relaxed mb-5 whitespace-pre-line">
                    {paragraph}
                  </p>
                ))}
              </article>
              <p className="mt-10 text-xs uppercase tracking-[0.14em] text-[hsl(var(--lxra-muted))]">
                &mdash; {post.author}
              </p>
            </Reveal>
          )}
        </div>
      </section>

      <CTASection testId="journal-post-cta-banner" buttonTestId="journal-post-cta-book-now-button" />
    </div>
  );
}
